import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Pagination as MuiPagination } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { capitalize } from 'lodash-es';
import {
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
  gridDetailPanelExpandedRowIdsSelector,
  gridPageCountSelector,
  GridPagination,
} from '@mui/x-data-grid-pro';
import {
  GridActionsCellItem,
  LinearLoadingOverlay,
  Table,
  TableDetailToggle,
} from '../../../components/table/Table';
import * as service from '../../../services/admin-service';
import { useAlerts } from '../../../hooks/use-alerts';
import { useGrowers } from '../../../hooks/use-growers';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { DownloadLink } from './DownloadLink.jsx';
import { DetailPanel } from './DetailPanel.jsx';
import { useFeatureFlag } from '../../../hooks/use-feature-flag';

function Pagination({ page, onPageChange, className }) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
    />
  );
}
function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export function Uploads({ type }) {
  const isUseLeaf = useFeatureFlag('UseLeaf');
  const fetching = useRef(false);
  const intervalId = useRef(null);
  const { selectedId: growerId } = useGrowers();
  const [isWorking, setWorking] = useState();
  const [uploads, setUploads] = useState();
  const [totalUploads, setTotalUploads] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [confirmReupload, setConfirmReupload] = useState();
  const { showError } = useAlerts();
  const apiRef = useGridApiRef();

  const api = useMemo(
    () => ({
      getRawFiles: service[`get${capitalize(type)}RawFiles`],
      getUploads: service[`get${capitalize(type)}Uploads`],
      upload: service[`upload${capitalize(type)}`],
    }),
    [type],
  );

  const fetchData = useCallback(() => {
    if (fetching.current) return;

    fetching.current = true;
    setWorking(true);

    api
      .getUploads(growerId, paginationModel.page, paginationModel.pageSize)
      .then((response) => {
        setUploads(response.data);
        setTotalUploads(response.total);
      })
      .catch(showError)
      .finally(() => {
        fetching.current = false;
        setWorking(false);
      });
  }, [
    api,
    growerId,
    paginationModel.page,
    paginationModel.pageSize,
    showError,
  ]);

  useEffect(() => {
    fetchData();

    function clearIt() {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    }

    clearIt();

    if (paginationModel.page === 0) {
      intervalId.current = setInterval(fetchData, 30000);
    }

    return clearIt;
  }, [fetchData, paginationModel.page]);

  const reupload = useCallback(() => {
    if (!confirmReupload) return;

    setConfirmReupload(false);
    setWorking(true);

    api
      .upload(confirmReupload.growerId, confirmReupload.file)
      .then((upload) => {
        setUploads((current) => [upload, ...current]);
        setTotalUploads((current) => current + 1);
      })
      .catch(showError)
      .finally(() => setWorking(false));
  }, [api, confirmReupload, showError]);

  return (
    <>
      <Box
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Table
          hideFooter={false}
          loading={isWorking || !uploads}
          columns={buildColumns()}
          rows={uploads || []}
          rowCount={totalUploads}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[100]}
          slots={{
            pagination: CustomPagination,
            loadingOverlay: LinearLoadingOverlay,
          }}
          getDetailPanelContent={({ row }) => (
            <DetailPanel row={row} getRawFiles={api.getRawFiles} />
          )}
          getDetailPanelHeight={() => 'auto'}
          apiRef={apiRef}
          onRowClick={(params) => apiRef.current.toggleDetailPanel(params.id)}
        />
      </Box>

      {confirmReupload && (
        <ConfirmationDialog
          title="Reprocess Upload"
          titleIcon={false}
          message="Are you sure you want to reprocess this upload?"
          handleClose={() => setConfirmReupload(false)}
          handleConfirm={reupload}
        />
      )}
    </>
  );

  function DetailsToggle({ id }) {
    const expandedRowIds = useGridSelector(
      apiRef,
      gridDetailPanelExpandedRowIdsSelector,
    );
    const isExpanded = expandedRowIds.includes(id);
    return <TableDetailToggle id={id} value={isExpanded} />;
  }

  function buildColumns() {
    return [
      {
        field: '__detail_panel_toggle__',
        headerName: '',
        width: 40,
        resizable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <DetailsToggle id={params.id} />,
      },
      {
        field: 'uploadedAt',
        headerName: 'Uploaded At',
        flex: 1.5,
        type: 'dateTime',
        valueFormatter: ({ value }) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }),
      },
      {
        field: 'uploadedBy',
        headerName: 'Uploaded By',
        flex: 0.75,
      },
      {
        field: 'operationName',
        headerName: 'Operation',
        flex: 1,
      },
      {
        field: 'file',
        headerName: 'File',
        flex: 1.5,
        renderCell: ({ row }) => <DownloadLink url={row.file} />,
      },
      isUseLeaf
        ? {
            field: 'status',
            headerName: 'Status',
            flex: 1.2,
            renderCell: ({ row }) => {
              const conversion = row.agi?.id ? row.agi : row.leaf;
              let label = null;
              if (row.agi?.id) label = 'AGI';
              else if (row.leaf?.id) label = 'Leaf';
              return buildConverterCell(conversion, label);
            },
          }
        : {
            field: 'agi',
            headerName: 'AGI',
            flex: 1.2,
            renderCell: ({ row }) => buildConverterCell(row.agi),
          },
      !isUseLeaf && {
        field: 'leaf',
        headerName: 'Leaf',
        flex: 1.2,
        renderCell: ({ row }) => buildConverterCell(row.leaf),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        getActions: ({ row }) => [
          <GridActionsCellItem
            title="Reupload"
            label="Reupload"
            icon={<RefreshIcon />}
            disabled={isWorking || !row.file}
            onClick={() => setConfirmReupload(row)}
          />,
        ],
      },
    ].filter(Boolean);
  }

  function getStatusIcon(status) {
    if (/error/i.test(status)) {
      return (
        <ErrorIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
      );
    }

    if (/complete/i.test(status)) {
      return (
        <CheckCircleIcon
          sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }}
        />
      );
    }

    return (
      <HourglassTopIcon
        sx={{ fontSize: 16, color: 'inherit', flexShrink: 0 }}
      />
    );
  }

  function buildConverterCell(conversion, label) {
    if (!conversion?.id) return null;

    return (
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
        {getStatusIcon(conversion.status)}
        <Box
          title={`${conversion.id}: ${conversion.message}`}
          sx={{
            flex: 1,
            ml: 0.5,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label ? `${label}: ${conversion.status}` : conversion.status}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {[conversion.rawFileCount, conversion.processingTime].join(' | ')}
          </Typography>
        </Box>
      </Box>
    );
  }
}
